import React from 'react';
import { observer } from 'mobx-react-lite';
import ReactTooltip from 'ati-ui-react/components/ReactTooltip';
import Popover from 'ati-ui-react/components/Popover';
import nanoid from 'nanoid';
import Link from 'ati-ui-react/components/Link';
import Delete from 'ati-ui-react/components/icons/Delete';
import Button from 'ati-ui-react/components/Button';
import { addMetric } from 'utils/addMetrics';
import theme from 'theme';
import { useStores } from 'hooks/useStores';
import useWindowSize from 'hooks/useWindowSize';
import { Wrapper, ActionButton, TooltipContainer, HeaderPopover } from './ArchiveButtonStyles';

const ArchiveButton = observer(
  ({
    advert,
    callback,
    className,
    small = false,
    setOpenedPopover = () => {},
    setHovered = () => {},
    isOpenedPopover = false,
    location,
  }) => {
    const {
      cabinetPageStore: { moveAdToArchive },
      advertsStore,
    } = useStores();

    const [width] = useWindowSize();
    const id = nanoid();

    const handleMoveAdToArchive = (options = {}) => (e) => {
      addMetric('tm-adv-archived');
      e.stopPropagation();
      advertsStore.setMiniCardAnimation(true);
      moveAdToArchive(advert._id, options, callback);
    };

    const handleVisiblePopover = (e) => {
      setOpenedPopover(!isOpenedPopover);
      setHovered(!isOpenedPopover);
    };

    const handleClick = (e) => {
      e.stopPropagation();
      handleVisiblePopover();
    };

    // TODO: взять значения из словаря
    const customSaleToArchivePopover = (
      <div className="popover-content-wrapper">
        <HeaderPopover>Укажите причину</HeaderPopover>
        <div className="popover-content">
          <div className="popover-text">
            Объявление&nbsp;будет&nbsp;снято&nbsp;с&nbsp;публикации
            и&nbsp;перемещено&nbsp;в&nbsp;архив.
          </div>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'sold_on_ati' })}
            simpleDashed
          >
            Продал на ATI.SU
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'sold_elsewhere' })}
            simpleDashed
          >
            Продал в другом месте
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'another_reason' })}
            simpleDashed
          >
            Другое
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleVisiblePopover}
            simpleDashed
            simpleGray
          >
            Не перемещать в архив
          </Button>
        </div>
      </div>
    );

    const customRentToArchivePopover = (
      <div className="popover-content-wrapper">
        <HeaderPopover>Укажите причину</HeaderPopover>
        <div className="popover-content">
          <div className="popover-text">
            Объявление&nbsp;будет&nbsp;снято&nbsp;с&nbsp;публикации
            и&nbsp;перемещено&nbsp;в&nbsp;архив.
          </div>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'stop_rent' })}
            simpleDashed
          >
            Перестал сдавать
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'transport_crash' })}
            simpleDashed
          >
            ТС неисправно
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'rent_elsewhere' })}
            simpleDashed
          >
            Выставил в другом месте
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'another_reason' })}
            simpleDashed
          >
            Другое
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleVisiblePopover}
            simpleDashed
            simpleGray
          >
            Не перемещать в архив
          </Button>
        </div>
      </div>
    );

    const customPurchaseToArchivePopover = (
      <div className="popover-content-wrapper">
        <HeaderPopover>Укажите причину</HeaderPopover>
        <div className="popover-content">
          <div className="popover-text">
            Объявление&nbsp;будет&nbsp;снято&nbsp;с&nbsp;публикации
            и&nbsp;перемещено&nbsp;в&nbsp;архив.
          </div>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'stop_purchase' })}
            simpleDashed
          >
            Купил ТС на АТИ
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'purchase_elsewhere' })}
            simpleDashed
          >
            Купил в другом месте
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'another_reason' })}
            simpleDashed
          >
            Другое
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleVisiblePopover}
            simpleDashed
            simpleGray
          >
            Не перемещать в архив
          </Button>
        </div>
      </div>
    );

    const customProposalsRentToArchivePopover = (
      <div className="popover-content-wrapper">
        <HeaderPopover>Укажите причину</HeaderPopover>
        <div className="popover-content">
          <div className="popover-text">
            Объявление&nbsp;будет&nbsp;снято&nbsp;с&nbsp;публикации
            и&nbsp;перемещено&nbsp;в&nbsp;архив.
          </div>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'stop_rent_proposal' })}
            simpleDashed
          >
            Арендовал ТС на АТИ
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'rent_proposal_elsewhere' })}
            simpleDashed
          >
            Арендовал в другом месте
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleMoveAdToArchive({ archive_reason: 'another_reason' })}
            simpleDashed
          >
            Другое
          </Button>
          <Button
            className="popover-btn"
            primary
            onClick={handleVisiblePopover}
            simpleDashed
            simpleGray
          >
            Не перемещать в архив
          </Button>
        </div>
      </div>
    );

    const getElement = () => {
      if (location === 'sale') return customSaleToArchivePopover;
      if (location === 'rent') return customRentToArchivePopover;
      if (location === 'purchase') return customPurchaseToArchivePopover;
      if (location === 'proposals-purchase') return customPurchaseToArchivePopover;
      if (location === 'proposals-rent') return customProposalsRentToArchivePopover;
      return null;
    };

    return (
      <Wrapper onClick={(e) => e.stopPropagation()} className={className}>
        <Popover
          className="confirmPopover"
          visible={isOpenedPopover}
          eventType="mousedown"
          color="default"
          element={getElement()}
          closeOnOverlayClick
          onClose={handleVisiblePopover}
          place="bottom"
          enableAdaptive
        >
          {small ? (
            <ActionButton onClick={handleClick} data-tip data-for={`trash-icon-${id}`}>
              <Link>
                <Delete color={theme.colors.color_red} width={17} height={18} />
              </Link>
              <Link small attention type="pseudo">
                <span className="delete-text">В архив</span>
              </Link>
            </ActionButton>
          ) : (
            <ActionButton onClick={handleClick}>
              <Button primary={false} size="small">
                {`${width > 512 ? 'Переместить ' : ''}в архив`}
              </Button>
            </ActionButton>
          )}
        </Popover>
        {!isOpenedPopover && (
          <TooltipContainer>
            <ReactTooltip id={`trash-icon-${id}`} effect="solid" place="top">
              Переместить объявление
              <br />в архив
            </ReactTooltip>
          </TooltipContainer>
        )}
      </Wrapper>
    );
  },
);

export default ArchiveButton;
